import get from 'lodash/get';

import {errors, isEmptyGuestsError, isGuestError} from './errorEvents';
import {labelMaker} from '../../server/utils/labelMaker';

function getTotalGuests({adults = 0, children = 0}) {
    return adults + children;
}

function getMaxGuestsLabel({sleeps, listingHouseRules, translationMaxGuests} = {}) {
    const {adults} = listingHouseRules || {};
    const guestsLabel = labelMaker(translationMaxGuests, sleeps);

    if (adults && adults !== sleeps) {
        return `${guestsLabel}`;
    }

    return guestsLabel;
}

function hasGuests({adults = 0, children = 0}) {
    return getTotalGuests({adults, children}) > 0;
}

function hasTooManyAdults({adults = 0, maxAdults = 0} = {}) {
    // Return false if maxAdults is 0 since that's an unsupported use case
    return !!(maxAdults && adults > maxAdults);
}

function hasTooManyChildren({children = 0, maxChildren = 0} = {}) {
    return !!(children > maxChildren);
}

function hasTooManyGuests({adults = 0, children = 0, sleeps = 0}) {
    return getTotalGuests({adults, children}) > sleeps;
}

function hasValidPets({hasPets = false, petsAllowed = false}) {
    return !hasPets || (hasPets && petsAllowed);
}

function hasAdults({adults = 0}) {
    return adults > 0;
}

function hasAdultSupervision({adults = 0, children = 0}) {
    return !(children > 0 && adults === 0);
}

function hasUnallowedChildren({children, childrenAllowed} = {}) {
    return !!(children && !childrenAllowed);
}

function hasValidGuestCount({
    adults = 0,
    children = 0,
    maxAdults = 0,
    maxChildren = 0,
    sleeps = 0,
    isEmptyValid = true
}) {
    // check if zero guests and zero guests is an invalid state
    if (!isEmptyValid && !hasGuests({adults, children})) {
        return false;
    }

    const validTotalGuestCount = !hasTooManyGuests({adults, children, sleeps});
    const validAdults =
        hasAdultSupervision({adults, children}) && !hasTooManyAdults({adults, maxAdults});
    const validChildren = !hasTooManyChildren({children, maxChildren});

    return validAdults && validChildren && validTotalGuestCount;
}

function hasValidGuests({
    adults = 0,
    children = 0,
    hasPets = false,
    maxAdults = 0,
    maxChildren = 0,
    sleeps = 0,
    petsAllowed = false,
    isEmptyValid = true,
    error = {} // most likely a quote error
}) {
    // check if there's a quote error
    if (isGuestError(error) && !(isEmptyValid && isEmptyGuestsError(error))) {
        return false;
    }

    // otherwise, check the usual suspects
    const validGuestCount = hasValidGuestCount({
        adults,
        children,
        maxAdults,
        maxChildren,
        sleeps,
        isEmptyValid
    });
    const validPets = hasValidPets({hasPets, petsAllowed});

    return validGuestCount && validPets;
}

function getSpaceLeft({adults, children, sleeps}) {
    const spaceMargin = sleeps - adults - children;
    return Math.max(0, spaceMargin);
}

function getChildrenAllowedFromHouseRules({houseRules} = {}) {
    // default to true if value is undefined
    return get(houseRules, 'children.allowed', true);
}

function getMaxAdultsFromHouseRules({houseRules, sleeps} = {}) {
    // default to # sleeps if max is null, undefined, or 0
    return (
        get(houseRules, 'maxOccupancy.adults') || get(houseRules, 'maxOccupancy.guests') || sleeps
    );
}

function getMaxChildrenFromHouseRules({houseRules, sleeps} = {}) {
    // default to # sleeps - 1, since children must be accompanied by an adult
    const childrenAllowed = getChildrenAllowedFromHouseRules({houseRules});
    return !childrenAllowed ? 0 : sleeps && sleeps - 1;
}

/* eslint-disable complexity */
function getGuestErrorObject({
    adults = 0,
    children = 0,
    hasPets = false,
    maxAdults = 0,
    maxChildren = 0,
    petsAllowed = false,
    sleeps = 0,
    translations = {}
}) {
    let error = null;
    let label;

    const validGuestCount = !hasTooManyGuests({adults, children, sleeps});
    const validPets = hasValidPets({hasPets, petsAllowed});

    if (!validGuestCount) {
        error = errors.OVER_MAX_OCCUPANCY;
        label = labelMaker(translations[error.translationKey], sleeps);
    } else if (hasUnallowedChildren({children, childrenAllowed: maxChildren > 0})) {
        error = errors.CHILDREN_NOT_ALLOWED;
        label = labelMaker(translations[error.translationKey]);
    } else if (!hasAdultSupervision({adults, children})) {
        error = errors.NEEDS_ADULTS;
        label = translations[error.translationKey];
    } else if (hasTooManyAdults({adults, maxAdults})) {
        error = errors.TOO_MANY_ADULTS;
        label = labelMaker(translations[error.translationKey], {MAX: maxAdults});
    } else if (!validPets) {
        error = errors.PETS_NOT_ALLOWED;
        label = translations[error.translationKey];
    } else if (!adults) {
        error = errors.ENTER_GUESTS;
        label = translations[error.translationKey];
    }

    if (error && label) {
        return {...error, label};
    }
    return error;
}
/* eslint-enable complexity */

export default hasGuests;
export {
    getChildrenAllowedFromHouseRules,
    getGuestErrorObject,
    getMaxAdultsFromHouseRules,
    getMaxChildrenFromHouseRules,
    getMaxGuestsLabel,
    getSpaceLeft,
    getTotalGuests,
    hasAdults,
    hasAdultSupervision,
    hasUnallowedChildren,
    hasGuests,
    hasTooManyAdults,
    hasTooManyChildren,
    hasTooManyGuests,
    hasValidGuests,
    hasValidGuestCount,
    hasValidPets
};
